import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { AXIOS_INSTANCE, customInstance } from '../../services/api-client'
import type {
  RevenueRedshiftConnectorOption,
  RevenueRedshiftDataSourceConfig,
  RevenueRedshiftDataSourceUpdate,
} from '../revenue-close/redshiftDataSourceTypes'
import type {
  AgentReview,
  Asc606Health,
  Asc606RevenueWaterfallRun,
} from './types'

const BASE = '/api/asc-606-revenue-waterfall'

export const asc606Keys = {
  all: ['asc-606-revenue-waterfall'] as const,
  run: () => [...asc606Keys.all, 'run'] as const,
  health: () => [...asc606Keys.all, 'health'] as const,
  config: () => [...asc606Keys.all, 'config'] as const,
  connectors: () => [...asc606Keys.all, 'connectors'] as const,
  agentReview: () => [...asc606Keys.all, 'agent-review'] as const,
}

export function useAsc606RevenueWaterfallRun(options?: { enabled?: boolean }) {
  return useQuery<Asc606RevenueWaterfallRun>({
    queryKey: asc606Keys.run(),
    queryFn: () => customInstance({ url: `${BASE}/run`, method: 'GET' }),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}

export function useAsc606Health() {
  return useQuery<Asc606Health>({
    queryKey: asc606Keys.health(),
    queryFn: () => customInstance({ url: `${BASE}/health`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useAsc606DataSourceConfig() {
  return useQuery<RevenueRedshiftDataSourceConfig>({
    queryKey: asc606Keys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useAsc606AvailableConnectors() {
  return useQuery<RevenueRedshiftConnectorOption[]>({
    queryKey: asc606Keys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useSetAsc606DataSource() {
  const queryClient = useQueryClient()
  return useMutation<RevenueRedshiftDataSourceConfig, Error, RevenueRedshiftDataSourceUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: asc606Keys.all })
    },
  })
}

export function useClearAsc606DataSource() {
  const queryClient = useQueryClient()
  return useMutation<RevenueRedshiftDataSourceConfig, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: asc606Keys.all })
    },
  })
}

export function useTestAsc606DataSource() {
  return useMutation<Asc606Health, Error, RevenueRedshiftDataSourceUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  })
}

export function useAsc606AgentReview() {
  return useMutation<AgentReview, Error, { use_llm?: boolean }>({
    mutationKey: asc606Keys.agentReview(),
    mutationFn: body =>
      customInstance({
        url: `${BASE}/agent/review`,
        method: 'POST',
        data: { use_llm: body.use_llm ?? false },
      }),
  })
}

export async function downloadAsc606Export(exportType: 'journal' | 'waterfall') {
  const response = await AXIOS_INSTANCE.get(`${BASE}/exports/${exportType}`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename =
    match?.[1] || (exportType === 'journal'
      ? 'ASC606_Revenue_Waterfall_JE_Apr2025.csv'
      : 'OUTPUT_EC03d_Revenue_Waterfall_Apr2025.csv')
  const url = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
